import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

ChartJS.register(...registerables);

const utahData = "/img/UTDT_Logo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

const EngTable16 = ({ data }) => {
  const pdfRef = useRef();

  // Clean Data + Auto Compute Growth
  const processed = data.map((item) => {
    const start = Number(item.year_2018);
    const end = Number(item.year_2024);
    const pct = (((end - start) / start) * 100).toFixed(2);

    return {
      ...item,
      pct,
      values: [
        Number(item.year_2018),
        Number(item.year_2019),
        Number(item.year_2020),
        Number(item.year_2021),
        Number(item.year_2022),
        Number(item.year_2023),
        Number(item.year_2024),
      ],
    };
  });

  // KPI Insights
  const totalGrowth = processed.reduce(
    (sum, row) => sum + (Number(row.year_2024) - Number(row.year_2018)),
    0,
  );

  const avgGrowth = (totalGrowth / processed.length).toFixed(2);

  const highestGrowthItem = processed.reduce((prev, curr) =>
    curr.pct > prev.pct ? curr : prev,
  );

  // Chart Configuration
  const yearLabels = ["2018", "2019", "2020", "2021", "2022", "2023", "2024"];
  const chartData = {
    labels: processed.map((item) => item.education_label),
    datasets: yearLabels.map((year, index) => ({
      label: `Year ${year}`,
      data: processed.map((row) => row.values[index]),
      backgroundColor: `#B7950B`,
      borderColor: `#B7950B`,
      borderWidth: 1,
    })),
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "Employment Growth: Less than High School (2018–2024)",
        font: { size: 18, weight: "bold" },
      },
    },
    scales: {
      y: { title: { display: true, text: "Employment" } },
      x: { title: { display: true, text: "Education Level" } },
    },
  };

  // PDF Function
 const downloadPDF = () => {
   const input = pdfRef.current;
   html2canvas(input, {
     scale: 2,
     useCORS: true,
     backgroundColor: "#ffffff",
     scrollY: -window.scrollY,
   }).then((canvas) => {
     const imgData = canvas.toDataURL("image/png");
     const pdf = new jsPDF("p", "mm", "a4");
     const pageWidth = pdf.internal.pageSize.getWidth();
     const pageHeight = pdf.internal.pageSize.getHeight();
     const imgWidth = pageWidth - 10;
     const imgHeight = (canvas.height * imgWidth) / canvas.width;

     let heightLeft = imgHeight;
     let position = 0;
     let pageNumber = 1;

     const addHeader = () => {
       const logoWidth = 20; // ideal header width
       const logoHeight = 20; // adjust ratio if needed

       pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
       pdf.setFontSize(26);
       pdf.text("UTahData.org", 95, 22);
       pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
       pdf.line(10, 35, 200, 35);
     };

     const addFooter = (pageNumber) => {
       pdf.setFontSize(9);
       const footerY = pageHeight - 10;
       pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
       pdf.text(
         `Downloaded on: ${new Date().toLocaleDateString()}`,
         10,
         footerY,
       );
       pdf.text(
         `Page ${pageNumber}`,
         pageWidth - 10,
         footerY,
         null,
         null,
         "right",
       );
     };

     // First page
     addHeader();
     pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
     heightLeft -= pageHeight - 32;

     // Add more pages if needed
     while (heightLeft > 0) {
       pdf.addPage();
       pageNumber++;
       addHeader();
       position = heightLeft - imgHeight;
       pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
       heightLeft -= pageHeight - 32;
       addFooter(pageNumber);
     }

     // Final footer
     // ===== FINAL FOOTER SAFE AREA =====

     const footerTopY = pageHeight - 40; // start footer 40mm from bottom
     const footerContentY = pageHeight - 28; // logos row
     const footerBottomTextY = pageHeight - 10; // bottom text row

     // Top Divider Line
     pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

     // UtahData logo
     const utahWidth = 18;
     const utahHeight = 18;
     pdf.addImage(
       utahData,
       "PNG",
       20,
       footerContentY - 8,
       utahWidth,
       utahHeight,
     );

     // Built by
     pdf.setFontSize(10);
     pdf.text("Built by", 45, footerContentY);

     // BODEX logo
     const bodexWidth = 36;
     const bodexHeight = bodexWidth * 0.3;
     pdf.addImage(
       bodex,
       "PNG",
       60,
       footerContentY - 6,
       bodexWidth,
       bodexHeight,
     );

     // Using text
     pdf.text("using", 102, footerContentY);

     // KPI logo
     pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

     // Version
     pdf.text("20250916.v3.198", 140, footerContentY);

     // Bottom Info Line
     pdf.setFontSize(8);
     pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
     pdf.text(
       `Downloaded on: ${new Date().toLocaleDateString()}`,
       pageWidth - 10,
       footerBottomTextY,
       null,
       null,
       "right",
     );

     pdf.save("EngTable16.pdf");
   });
 };

  return (
    <>
      {/* Page Content */}
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold text-center mb-4">
          Employment Growth for "Less than High School" (2018–2024)
        </h2>

        {/* KPI Tiles */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-indigo-100 rounded-lg shadow">
            <p className="font-semibold text-indigo-900">📈 Average Growth</p>
            <p className="text-xl font-bold">{avgGrowth} Jobs</p>
          </div>

          <div className="p-4 bg-green-100 rounded-lg shadow">
            <p className="font-semibold text-green-900">
              🚀 Highest Growth Category
            </p>
            <p className="text-sm text-gray-800">
              {highestGrowthItem.education_label}
            </p>
            <p className="font-bold text-green-800">{highestGrowthItem.pct}%</p>
          </div>

          <div className="p-4 bg-yellow-100 rounded-lg shadow">
            <p className="font-semibold text-yellow-900">📊 Total Growth</p>
            <p className="text-xl font-bold">
              {totalGrowth.toLocaleString("en-US")} Jobs
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full text-left shadow-md border rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-3">Education Level</th>
                    {yearLabels.map((y) => (
                      <th key={y}>Year {y}</th>
                    ))}
                    <th>Growth %</th>
                  </tr>
                </thead>
                <tbody>
                  {processed.map((row, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="font-semibold p-2">
                        {row.education_label}
                      </td>
                      {row.values.map((v, i) => (
                        <td key={i}>{v.toLocaleString("en-US")}</td>
                      ))}
                      <td className="text-green-700 font-semibold">
                        {row.pct}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[600px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
};

export default EngTable16;
